import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Phone, Mail, MessageCircle, MessageSquare, ShieldCheck, FileText, Flag } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { openCookieSettings } from "@/lib/consent";
import { toast } from "sonner";

function ReportForm({ info, onDone }) {
  const { requireAuth } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = () => requireAuth(async () => {
    if (text.trim().length < 5) return toast.error("Përshkruaje problemin pak më gjatë");
    setBusy(true);
    try { await api.post("/reports", { target_type: "problem", target_id: "site", reason: "Problem në faqe", note: text.trim() }); toast.success("Faleminderit! Problemi u dërgua te admini"); setText(""); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  });
  return (
    <div className="space-y-3" data-testid="report-problem-form">
      {info?.trim() && <p className="text-sm text-slate-600 whitespace-pre-wrap">{info}</p>}
      <textarea data-testid="report-problem-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={2000} placeholder="Shkruaj çka nuk punon ose çka ka ndodhur..." className="w-full rounded-xl border border-slate-200 p-3 text-sm" />
      <button data-testid="report-problem-send" onClick={send} disabled={busy} className="w-full h-11 rounded-xl bg-rose-500 text-white font-bold text-sm disabled:opacity-60">{busy ? "Duke dërguar..." : "Dërgo raportin"}</button>
    </div>
  );
}
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const IG = (
  <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden="true">
    <defs>
      <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stopColor="#fdf497" />
        <stop offset="5%" stopColor="#fdf497" />
        <stop offset="45%" stopColor="#fd5949" />
        <stop offset="60%" stopColor="#d6249f" />
        <stop offset="90%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#ig-grad)" />
    <circle cx="12" cy="12" r="4.3" fill="none" stroke="#fff" strokeWidth="1.9" />
    <circle cx="17.2" cy="6.8" r="1.25" fill="#fff" />
  </svg>
);

const TT = (
  <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="6" fill="#010101" />
    <path d="M15.1 6.1c.32 1.4 1.15 2.4 2.5 2.6v2.05c-.92 0-1.82-.28-2.55-.78v4.2c0 2.42-2.02 4.22-4.35 3.88-1.86-.27-3.2-1.93-3.08-3.8.12-1.82 1.72-3.26 3.55-3.2.23 0 .45.03.66.08v2.12a1.8 1.8 0 0 0-.66-.13c-.95 0-1.68.82-1.58 1.78.08.82.8 1.45 1.63 1.4.86-.03 1.48-.75 1.48-1.6V6.1h2.4z" fill="#fff" />
  </svg>
);

const YT = (
  <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden="true">
    <rect x="1.5" y="5" width="21" height="14" rx="4.5" fill="#FF0000" />
    <path d="M10 8.7l5.3 3.3L10 15.3V8.7z" fill="#fff" />
  </svg>
);

function waLink(num) {
  return `https://wa.me/${(num || "").replace(/[^0-9]/g, "")}`;
}
function viberLink(num) {
  return `viber://chat?number=${encodeURIComponent((num || "").replace(/[^0-9+]/g, ""))}`;
}

export function Footer() {
  const [s, setS] = useState(null);
  const [modal, setModal] = useState(null);
  const nav = useNavigate();
  const { requireAuth } = useAuth();

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const social = s?.social || {};
  const contact = s?.contact || {};
  const pages = s?.pages || {};

  const socials = [
    { key: "instagram", label: "Instagram", icon: IG, cfg: social.instagram },
    { key: "tiktok", label: "TikTok", icon: TT, cfg: social.tiktok },
    { key: "youtube", label: "YouTube", icon: YT, cfg: social.youtube },
  ];

  const links = [
    { key: "contact", label: "Kontakt", icon: Phone },
    { key: "privacy", label: "Privatësia", icon: ShieldCheck },
    { key: "terms", label: "Kushtet", icon: FileText },
    { key: "report", label: "Raporto problem", icon: Flag },
  ];

  const pageKey = { privacy: "privacy", terms: "terms", report: "report" };
  const activeSocials = socials.filter((soc) => soc.cfg?.active && soc.cfg?.value);
  const activeLinks = links.filter((l) => l.key === "contact" || pages[l.key]?.active);

  const modalContent = () => {
    if (modal === "contact") {
      const btns = [
        { k: "chat", icon: MessageSquare, label: "SMS", bg: "bg-sky-50", dot: "bg-sky-500",
          onClick: (e) => { e.preventDefault(); setModal(null); requireAuth(() => api.get("/support-user").then((r) => nav(`/mesazhe/${r.data.id}`)).catch(() => {})); } },
        contact.whatsapp?.active && contact.whatsapp?.value && { k: "whatsapp", icon: MessageCircle, label: "WhatsApp", href: waLink(contact.whatsapp.value), bg: "bg-green-50", dot: "bg-green-500" },
        contact.viber?.active && contact.viber?.value && { k: "viber", icon: Phone, label: "Viber", href: viberLink(contact.viber.value), bg: "bg-purple-50", dot: "bg-purple-600" },
        contact.phone?.active && contact.phone?.value && { k: "phone", icon: Phone, label: "Telefon", href: `tel:${contact.phone.value.replace(/\s/g, "")}`, bg: "bg-blue-50", dot: "bg-blue-600" },
        contact.email?.active && contact.email?.value && { k: "email", icon: Mail, label: "Email", href: `mailto:${contact.email.value}`, bg: "bg-red-50", dot: "bg-red-500" },
      ].filter(Boolean);
      if (!btns.length) return <p className="text-sm text-slate-500 text-center">Të dhënat e kontaktit nuk janë vendosur ende.</p>;
      return (
        <div className="flex justify-center gap-2">
          {btns.map((b) => {
            const Icon = b.icon;
            return (
              <a key={b.k} data-testid={`contact-btn-${b.k}`} href={b.href || "#"} onClick={b.onClick} target={b.href ? "_blank" : undefined} rel="noreferrer" className={`${b.bg} flex-1 min-w-0 rounded-2xl py-3 flex flex-col items-center gap-2 active:scale-95 transition-transform`}>
                <span className={`${b.dot} w-14 h-14 rounded-full flex items-center justify-center text-white`}><Icon className="w-7 h-7" /></span>
                <span className="text-xs font-semibold text-slate-800">{b.label}</span>
              </a>
            );
          })}
        </div>
      );
    }
    const cfg = pages[pageKey[modal]];
    if (modal === "report") return <ReportForm info={cfg?.value} onDone={() => setModal(null)} />;
    if (!cfg?.value) return <p className="text-sm text-slate-500">Përmbajtja nuk është shtuar ende.</p>;
    return <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{cfg.value}</p>;
  };

  const modalTitle = modal === "contact" ? "Kontakt" : links.find((l) => l.key === modal)?.label;

  return (
    <footer data-testid="site-footer" className="mt-8 px-6 pt-8 pb-6 border-t border-slate-100 bg-white">
      {/* Brand */}
      <Link to="/" className="flex items-center" data-testid="footer-brand">
        <img src="/logo-full.png" alt="GjejShqip – Gjej çka të duhet." style={{ height: 56, width: "auto" }} draggable={false} />
      </Link>

      {activeSocials.length > 0 && <>
      <h3 className="text-xs font-bold tracking-widest text-slate-400 uppercase mt-8 mb-4">Na ndiqni</h3>
      <div className="flex items-start gap-7">
        {activeSocials.map((soc) => (
          <a key={soc.key} data-testid={`footer-social-${soc.key}`} href={soc.cfg.value} target="_blank" rel="noopener noreferrer" aria-label={soc.label} className="flex flex-col items-center active:scale-95 transition-transform">
            <span className="w-14 h-14 rounded-2xl overflow-hidden shadow-sm">{soc.icon}</span>
            <span className="text-sm font-medium text-slate-700 mt-2">{soc.label}</span>
          </a>
        ))}
      </div>
      </>}

      {activeLinks.length > 0 && <>
      <h3 className="text-xs font-bold tracking-widest text-slate-400 uppercase mt-8 mb-4">Linket</h3>
      <div className="grid grid-cols-4 gap-2">
        {activeLinks.map((l) => {
          const Icon = l.icon;
          return (
            <button key={l.key} data-testid={`footer-link-${l.key}`} onClick={() => setModal(l.key)} className="flex flex-col items-center gap-2 text-center active:scale-95 transition-transform">
              <Icon className="w-7 h-7 text-emerald-600" strokeWidth={1.8} />
              <span className="text-[13px] font-medium text-slate-700 leading-tight">{l.label}</span>
            </button>
          );
        })}
      </div>
      </>}

      <div className="border-t border-slate-100 mt-7 pt-5">
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-sm">
          <Link to="/politika-cookies" data-testid="footer-cookie-policy" className="text-emerald-700 font-semibold underline">Politika e Cookies</Link>
          <button type="button" data-testid="footer-cookie-settings" onClick={openCookieSettings} className="text-emerald-700 font-semibold underline">Cilësimet e cookies</button>
        </div>
        <p className="text-sm text-slate-400">© {new Date().getFullYear()} GjejShqip — Të gjitha të drejtat e rezervuara.</p>
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-sm rounded-2xl max-h-[85vh] flex flex-col" data-testid="footer-modal">
          {modal === "contact" ? (
            <DialogHeader className="items-center text-center">
              <span className="flex items-center" data-testid="contact-brand">
                <img src="/logo-full.png" alt="GjejShqip" style={{ height: 40, width: "auto" }} draggable={false} />
              </span>
              <DialogTitle className="text-2xl font-extrabold text-slate-900 mt-2">Na kontakto</DialogTitle>
              <p className="text-sm text-slate-500">Zgjidh mënyrën e kontaktit që preferon</p>
            </DialogHeader>
          ) : (
            <DialogHeader>
              <DialogTitle>{modalTitle}</DialogTitle>
            </DialogHeader>
          )}
          <div className="mt-1 flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1" data-testid="footer-modal-scroll">{modalContent()}</div>
          {(modal === "privacy" || modal === "terms") && (
            <button type="button" data-testid="footer-modal-understood" onClick={() => setModal(null)} className="w-full shrink-0 rounded-xl h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold active:scale-95 transition-transform">E kuptova</button>
          )}
        </DialogContent>
      </Dialog>
    </footer>
  );
}

export default Footer;
